import { useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import Loading from "~/components/Loading";
import { ManifoldNextGenAppHeader } from "~/components/ManifoldNextGenAppHeader";
import { NextGenSwitchPrompt } from "~/components/NextGenSwitchPrompt";
import { NextGenHeaderProvider } from "~/contexts/nextgen-header";
import {
  useForceManifoldNextGenFeature,
  useManifoldNextGenFeature,
} from "~/hooks/useManifoldNextGenFeature";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { ROUTE_404 } from "~/router/constants";

const DARK_MODE_KEY = "nextgen-dark-mode";

export function ManifoldNextGenLayout() {
  const feature = useManifoldNextGenFeature();
  const forceFeature = useForceManifoldNextGenFeature();
  const [preferences, updatePreferences] = useUserPreferences();
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(() => localStorage.getItem(DARK_MODE_KEY) === "true");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem(DARK_MODE_KEY, String(isDark));
  }, [isDark]);

  useEffect(() => {
    document.body.dataset.theme = "manifold-nextgen";
    return () => {
      delete document.body.dataset.theme;
    };
  }, []);

  if (feature.isLoading || forceFeature.isLoading) {
    return <Loading />;
  }

  if (!feature.enabled && !forceFeature.enabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  const isLockedIntoNextGen = forceFeature.enabled;

  // Eligible for the new experience but not yet opted in: offer to switch
  // rather than dead-ending the link at a 404. Users locked into the new
  // experience skip this prompt entirely — there is no legacy fallback for them.
  if (!isLockedIntoNextGen && !preferences.optedIntoManifoldNextGen) {
    return (
      <NextGenSwitchPrompt
        onConfirm={() => updatePreferences({ optedIntoManifoldNextGen: true })}
        onCancel={() => navigate(ROUTE_404, { replace: true })}
      />
    );
  }

  const handleSwitchToClassic = () => {
    document.documentElement.classList.remove("dark");
    updatePreferences({ optedIntoManifoldNextGen: false });
    navigate("/");
  };

  return (
    <NextGenHeaderProvider>
      <QueryParamProvider adapter={ReactRouter6Adapter} options={{ removeDefaultsFromUrl: true }}>
        <div className="flex h-screen flex-col bg-background text-foreground">
          <ManifoldNextGenAppHeader
            isDark={isDark}
            onToggleDark={() => setIsDark((d) => !d)}
            onSwitchToClassic={isLockedIntoNextGen ? undefined : handleSwitchToClassic}
          />
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </QueryParamProvider>
    </NextGenHeaderProvider>
  );
}
